/**
 * Analitik pihak pertama: satu catatan per KUNJUNGAN halaman, dikirim ke
 * Django The Muslim (`POST /analytics/events`, `app: "knowledgex"`).
 *
 * ## Bentuk datanya
 *
 * `page_view` dikirim saat orang MENINGGALKAN halaman — pindah rute, tab
 * disembunyikan, atau dokumen ditutup — lengkap dengan `screen` dan
 * `duration_ms`. Satu baris = satu kunjungan beserta lamanya; `ts` = saat
 * kunjungan DIMULAI. Kembali ke tab yang sama membuka kunjungan baru bertanda
 * `props.resumed`, supaya bisa disaring.
 *
 * ## Selalu anonim
 *
 * JWT KnowledgeX (`kx.auth.v1`) TIDAK dikirim. Django tidak bisa
 * memverifikasinya — tanda tangannya milik Go — dan mengirim token sesi ke
 * layanan lain hanya menambah satu tempat ia bisa bocor. `user_id` di sisi
 * Django karenanya selalu kosong untuk web KnowledgeX.
 *
 * ## Tidak boleh mengganggu halaman
 *
 * Semua galat ditelan. Kiriman lewat `sendBeacon`/`fetch keepalive` dengan
 * `Content-Type: text/plain` — permintaan "sederhana" tanpa preflight CORS.
 * CSP di `nginx.conf` sudah mengizinkan `connect-src https:`.
 *
 * ## Kapan menyala
 *
 * Hanya di build produksi (`vite build`), dan tetap diam di host pengembang,
 * LAN, terowongan, dan staging (`*.radhsyn83.dev`) — staging memakai build
 * produksi juga, dan lalu lintas ujinya tidak boleh tercampur ke data asli.
 * `VITE_ANALYTICS_DEV=true` memaksanya menyala di mana pun (untuk menguji),
 * dan `VITE_ANALYTICS_URL` mengganti alamat tujuannya (mis. Django lokal).
 * `navigator.doNotTrack === "1"` dihormati: tidak ada yang dicatat.
 */

const ENDPOINT = import.meta.env.VITE_ANALYTICS_URL || 'https://api.the-muslim.app/analytics/events'
const DIPAKSA = import.meta.env.VITE_ANALYTICS_DEV === 'true'
const VERSI = import.meta.env.VITE_APP_VERSION || undefined
const HOST_DEV = /^(localhost|127\.0\.0\.1|\[?::1\]?|0\.0\.0\.0)$|\.local$|\.localhost$|\.trycloudflare\.com$|\.radhsyn83\.dev$|^192\.168\./

const KUNCI_PERANGKAT = 'tm_device_id'
const KUNCI_SESI = 'tm_session'
const SESI_HABIS_MS = 30 * 60 * 1000 // sesi berganti setelah 30 menit tanpa kegiatan
const INTERVAL_KIRIM_MS = 15_000
const MAKS_PER_BATCH = 100 // batas kontrak
const MAKS_ANTREAN = 500

let terpasang = false
let aktif = false
let antrean = [] // [{ sesi, e }]
let kini = null
let sebelumnya = null

// ── Penyimpanan: gagal diam-diam (mode pribadi Safari, kuota penuh) ───────

function baca(k) {
  try { return localStorage.getItem(k) } catch { return null }
}

function tulis(k, v) {
  try { localStorage.setItem(k, v) } catch { /* nilainya cuma tidak bertahan */ }
}

function uuid() {
  try {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    const b = crypto.getRandomValues(new Uint8Array(16))
    b[6] = (b[6] & 0x0f) | 0x40
    b[8] = (b[8] & 0x3f) | 0x80
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  }
}

let perangkatCadangan = null

function idPerangkat() {
  const ada = baca(KUNCI_PERANGKAT)
  if (ada) return ada
  perangkatCadangan ??= uuid()
  tulis(KUNCI_PERANGKAT, perangkatCadangan)
  return perangkatCadangan
}

/**
 * Id sesi yang berlaku sekarang, sekaligus menandai "masih ada kegiatan".
 * Di `localStorage`, bukan `sessionStorage`: dua tab adalah satu sesi.
 */
function idSesi() {
  const sekarang = Date.now()
  let id = null
  try {
    const s = JSON.parse(baca(KUNCI_SESI) || 'null')
    if (s && typeof s.id === 'string' && sekarang - Number(s.t) < SESI_HABIS_MS) id = s.id
  } catch { /* rusak = sesi baru */ }
  id ??= uuid()
  tulis(KUNCI_SESI, JSON.stringify({ id, t: sekarang }))
  return id
}

function bahasa() {
  const b = (baca('kx.locale') || document.documentElement.lang || '').slice(0, 2).toLowerCase()
  return /^[a-z]{2}$/.test(b) ? b : undefined
}

function zonaWaktu() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined } catch { return undefined }
}

// ── Kunjungan ─────────────────────────────────────────────────────────────

function mulai(kunci, screen, props) {
  kini = { kunci, screen: screen.slice(0, 200), props, mulai: performance.now(), ts: new Date().toISOString(), sesi: idSesi() }
}

function akhiri() {
  if (!kini) return
  const { kunci, screen, props, mulai: t0, ts, sesi } = kini
  kini = null
  sebelumnya = { kunci, screen, props }
  idSesi() // meninggalkan halaman juga kegiatan
  const e = { name: 'page_view', ts, screen, duration_ms: Math.max(0, Math.round(performance.now() - t0)) }
  if (Object.keys(props).length) e.props = props
  antrean.push({ sesi, e })
  if (antrean.length > MAKS_ANTREAN) antrean = antrean.slice(-MAKS_ANTREAN)
}

/**
 * Halaman baru terbuka — dipanggil tiap kali rute berganti, termasuk muat
 * pertama. Kunjungan sebelumnya ditutup dan masuk antrean.
 *
 * `kunci` = jalur mentah: dua slug berbeda dengan pola yang sama tetap dua
 * kunjungan, dan pemanggilan ulang untuk jalur yang sama (StrictMode) diabaikan.
 */
export function pageOpened(kunci, screen, props = {}) {
  if (!aktif) return
  try {
    if (kini?.kunci === kunci) return
    akhiri()
    mulai(kunci, screen, props)
  } catch { /* analitik tidak boleh mematahkan navigasi */ }
}

// ── Pengiriman ────────────────────────────────────────────────────────────

function kirim(lewatBeacon) {
  if (!antrean.length) return
  const isi = antrean
  antrean = []

  // `session_id` ada di tingkat batch → satu batch per sesi.
  const perSesi = new Map()
  for (const { sesi, e } of isi) {
    if (!perSesi.has(sesi)) perSesi.set(sesi, [])
    perSesi.get(sesi).push(e)
  }

  const dasar = { device_id: idPerangkat(), app: 'knowledgex', platform: 'web', app_version: VERSI, locale: bahasa(), tz: zonaWaktu() }

  for (const [sesi, semua] of perSesi) {
    for (let i = 0; i < semua.length; i += MAKS_PER_BATCH) {
      const events = semua.slice(i, i + MAKS_PER_BATCH)
      const badan = JSON.stringify({ ...dasar, session_id: sesi, events })
      let terkirim = false
      if (lewatBeacon && typeof navigator.sendBeacon === 'function') {
        try { terkirim = navigator.sendBeacon(ENDPOINT, new Blob([badan], { type: 'text/plain;charset=UTF-8' })) } catch { terkirim = false }
      }
      if (terkirim) continue
      try {
        fetch(ENDPOINT, {
          method: 'POST',
          body: badan,
          keepalive: true,
          credentials: 'omit',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        }).catch(() => {
          // Jaringan putus: kembalikan ke antrean untuk percobaan berikutnya.
          antrean = [...events.map((e) => ({ sesi, e })), ...antrean].slice(-MAKS_ANTREAN)
        })
      } catch { /* fetch tidak tersedia — dibuang */ }
    }
  }
}

function saatTersembunyi() {
  akhiri()
  kirim(true)
}

function saatTerlihat() {
  if (kini || !sebelumnya) return
  mulai(sebelumnya.kunci, sebelumnya.screen, { ...sebelumnya.props, resumed: true })
}

/** Pasang pelacak sekali per dokumen. Aman dipanggil berulang. */
export function installAnalytics() {
  if (terpasang || typeof window === 'undefined') return
  terpasang = true
  try {
    if (!DIPAKSA && (!import.meta.env.PROD || HOST_DEV.test(window.location.hostname))) return
    if (navigator.doNotTrack === '1') return
    aktif = true
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') saatTersembunyi()
      else saatTerlihat()
    })
    window.addEventListener('pagehide', saatTersembunyi)
    // Kembali dari bfcache: dokumen yang sama hidup lagi tanpa muat ulang.
    window.addEventListener('pageshow', (e) => { if (e.persisted) saatTerlihat() })
    window.setInterval(() => kirim(false), INTERVAL_KIRIM_MS)
  } catch {
    aktif = false
  }
}
